import cn from 'classnames';
import type { MouseEventHandler} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FacetData, FacetGroup } from '../model/Facets';
import { findGroupedFacet, isFacetGroup } from '../model/Search';
import type { Coords } from '../storelocator/StoreLocatorForm';
import { onElementBlur } from '../utils/onElementBlur';
import { FacetList, Facets } from './Facets';
import * as style from './SearchFacets.module.css';
import SelectedFacets from './SelectedFacets';

interface Props {
    facets: FacetData;
    totalResults: number;
    facetsPopover: FacetGroup | boolean;
    onFacetValueSelect(query: string): Promise<void> | void;
    onFacetValueUnselect?(query: string): Promise<void> | void;
    onLocationChange(location: Coords): Promise<void>;
    onFacetsCloseClicked(): void;
}

export default function SearchFacets({
    facets: { groupedFacets, breadcrumbs, any },
    totalResults,
    facetsPopover,
    onFacetValueSelect,
    onFacetValueUnselect = onFacetValueSelect,
    onLocationChange,
    onFacetsCloseClicked,
}: Props) {
    const visible = !!facetsPopover;
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // when popup opens, place focus within it
        if (visible) {
            popupRef.current?.querySelector('button')?.focus();
        }
    }, [visible]);

    const onClick: MouseEventHandler = useCallback(
        (e) => {
            // trick to hide the popup when clicking on the darkend background
            if (e.clientX > popupRef.current!.clientWidth) {
                onFacetsCloseClicked();
            }
        },
        [onFacetsCloseClicked],
    );

    if (!any) return null;

    return (
        <div
            tabIndex={-1}
            id="search-refinements"
            className={cn(style.facets, { [style.visible]: visible, [style.selectedFacet]: typeof facetsPopover === 'object' })}
            ref={popupRef}
            onBlur={onElementBlur(onFacetsCloseClicked)}
            onClick={onClick}
        >
            {typeof facetsPopover === 'object' ? (
                <>
                    <div className={style.topBar}>
                        <h2>{facetsPopover.name}</h2>
                    </div>
                    <div className={style.selectedFacets}>
                        <SelectedFacets
                            selectedFacets={breadcrumbs.filter((f) => f.facetCode === facetsPopover.code)}
                            onFacetValueUnselect={onFacetValueUnselect}
                        />
                    </div>
                    <FacetList facets={facetsPopover.facets} onFacetValueSelect={onFacetValueSelect} onLocationChange={onLocationChange} />
                </>
            ) : (
                <>
                    <div className={style.selectedFacets}>
                        <SelectedFacets selectedFacets={breadcrumbs} onFacetValueUnselect={onFacetValueUnselect} />
                    </div>
                    <Facets facets={groupedFacets} onFacetValueSelect={onFacetValueSelect} onLocationChange={onLocationChange} />
                </>
            )}
            <div className={style.applyButtonWrapper}>
                <button onClick={onFacetsCloseClicked}>Vis resultat ({totalResults})</button>
            </div>
        </div>
    );
}

/**
 * One of the following values:
 * * `FacetGroup`: Show single facet in popover
 * * `true`: Show all facets in popover
 * * `false`: Hide popover
 */
export function useFacetPopover(facets: FacetData | undefined) {
    const [facetsPopover, setFacetsPopover] = useState<FacetGroup | boolean>(false);

    useEffect(() => {
        setFacetsPopover((f) => (isFacetGroup(f) ? (findGroupedFacet(facets, f.code) ?? false) : f));
    }, [facets]);

    const showFacetsPopover = useCallback(() => setFacetsPopover(true), []);
    const hideFacetsPopover = useCallback(() => setFacetsPopover(false), []);

    return [facetsPopover, setFacetsPopover, showFacetsPopover, hideFacetsPopover] as const;
}
